'use client';

import { c as _c } from "react/compiler-runtime";
import type { CustomerFunnelStage, DemandChannel, DemandDeliveryActivity, DemandRole, DemandTactic } from '@sizzlenz/shared';
import { ListFilter } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { createEmptyDemandInspirationFilters, DemandInspirationFilters, matchesDemandInspirationFilters, type DemandInspirationFilterKey, type DemandInspirationFilterValue } from '@/components/demand/DemandInspirationFilters';
import { ExampleCard } from '@/components/demand/ExampleCard';
import { FEED_EXAMPLES } from '@/components/demand/feed-examples-data';
import { Button } from '@/components/ui/button';
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { useUrlSync } from '@/lib/hooks/useUrlSync';
const INSPIRATION_FEED_FILTERS = ['demandRoles', 'journeyStages', 'tactics', 'channels', 'deliveryActivities'] as const satisfies readonly DemandInspirationFilterKey[];
export function InspirationFeed() {
  const $ = _c(30);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      paramName: "roles",
      defaultValue: [],
      isArray: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      paramName: "stages",
      defaultValue: [],
      isArray: true
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      paramName: "tactics",
      defaultValue: [],
      isArray: true
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      paramName: "channels",
      defaultValue: [],
      isArray: true
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      filters: {
        demandRoles: t0,
        journeyStages: t1,
        tactics: t2,
        channels: t3,
        deliveryActivities: {
          paramName: "execution",
          defaultValue: [],
          isArray: true
        }
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    values,
    handlers,
    isReady
  } = useUrlSync(t4);
  const t5 = values.demandRoles as DemandRole[];
  const t6 = values.journeyStages as CustomerFunnelStage[];
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const t8 = values.tactics as DemandTactic[];
  const t9 = values.channels as DemandChannel[];
  const t10 = values.deliveryActivities as DemandDeliveryActivity[];
  let t11;
  if ($[6] !== t10 || $[7] !== t5 || $[8] !== t6 || $[9] !== t8 || $[10] !== t9) {
    t11 = {
      demandRoles: t5,
      journeyStages: t6,
      demandObjectives: t7,
      tactics: t8,
      channels: t9,
      deliveryActivities: t10
    };
    $[6] = t10;
    $[7] = t5;
    $[8] = t6;
    $[9] = t8;
    $[10] = t9;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const filters = t11;
  let t12;
  if ($[12] !== handlers) {
    t12 = next => {
      handlers.demandRoles(next.demandRoles);
      handlers.journeyStages(next.journeyStages);
      handlers.tactics(next.tactics);
      handlers.channels(next.channels);
      handlers.deliveryActivities(next.deliveryActivities);
    };
    $[12] = handlers;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const handleFiltersChange = t12;
  let t13;
  if ($[14] !== filters) {
    t13 = FEED_EXAMPLES.filter(item => matchesDemandInspirationFilters(item.example.attributes, filters, INSPIRATION_FEED_FILTERS));
    $[14] = filters;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const filteredExamples = t13;
  let t14;
  if ($[16] !== handleFiltersChange) {
    t14 = () => handleFiltersChange(createEmptyDemandInspirationFilters());
    $[16] = handleFiltersChange;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const clearFilters = t14;
  let t15;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <header className="sticky top-0 z-10 border-b border-gray-200 bg-white/95 backdrop-blur-sm"><div className="mx-auto max-w-4xl px-4 py-4 text-center sm:px-6"><h1 className="text-2xl font-black tracking-tight text-gray-950 sm:text-3xl">Get Inspired, Take Action</h1></div></header>;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== filteredExamples.length || $[20] !== filters || $[21] !== handleFiltersChange) {
    t16 = <DemandInspirationFilters value={filters} onChange={handleFiltersChange} resultCount={filteredExamples.length} totalCount={FEED_EXAMPLES.length} visibleFilters={INSPIRATION_FEED_FILTERS} />;
    $[19] = filteredExamples.length;
    $[20] = filters;
    $[21] = handleFiltersChange;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== clearFilters || $[24] !== filteredExamples || $[25] !== isReady) {
    t17 = !isReady ? <div className="mt-6 flex flex-col items-center gap-5" aria-busy="true" aria-live="polite">{Array.from({
        length: 3
      }).map(_temp)}</div> : filteredExamples.length > 0 ? <div className="mt-6 flex flex-col items-center gap-5">{filteredExamples.map(_temp2)}</div> : <Empty className="mt-6 border border-gray-200 bg-white"><EmptyHeader><EmptyMedia variant="icon"><ListFilter aria-hidden="true" /></EmptyMedia><EmptyTitle>No examples match yet</EmptyTitle><EmptyDescription>Try removing one or two filters to broaden the inspiration shown.</EmptyDescription></EmptyHeader><EmptyContent><Button type="button" className="cursor-pointer" onClick={clearFilters}>Clear all filters</Button></EmptyContent></Empty>;
    $[23] = clearFilters;
    $[24] = filteredExamples;
    $[25] = isReady;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== t16 || $[28] !== t17) {
    t18 = <div className="min-h-screen bg-gray-50">{t15}<main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">{t16}{t17}</main></div>;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  return t18;
}
function _temp2(item_0) {
  return <ExampleCard key={item_0.example.id} example={item_0.example} variant={item_0.variant} postMeta={item_0.postMeta} analysis={item_0.analysis} widthClass={item_0.variant === "intelligence" ? "w-full" : "w-full max-w-md"} className={item_0.variant === "intelligence" ? "w-full" : "w-full max-w-md"} />;
}
function _temp(_, index) {
  return <div key={index} className="h-72 w-full max-w-md animate-pulse rounded-2xl border border-gray-200 bg-white" />;
}